import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsNativeEmbed } from '../../../../music24/navigation/hooks/useIsNativeEmbed';
import { useAccountId } from '../../../../../redux/slices/appInfo/hooks';
import { songFilesDeepLink } from '../songFilesDeepLink';
import { catalogSongPath, SongLyricsView, SongPageSection } from './catalogSongPath';

/** Sends the web view to a link. A seam for the tests; the page uses `window.location.assign`. */
type NavigateWindow = (url: string) => void;

function navigateWindow(url: string): void {
  window.location.assign(url);
}

/**
 * Opens the song page of a catalog row (design song-page a1, a3).
 *
 * In a browser it goes to `/catalog/song/<uuid>`, with the section to scroll to. Inside the iOS and
 * Android web view (`useIsNativeEmbed`) it sends the app the `songFiles` deep link with the section:
 * a new app opens its native song page at that section, an old app opens its song files sheet. The
 * new `song` link of the design opens nothing in an old app, so the web sends `songFiles` until every
 * app knows `song`.
 */
export function useOpenSongPage(navigateNative: NavigateWindow = navigateWindow) {
  const navigate = useNavigate();
  const isNativeEmbed = useIsNativeEmbed();
  const accountId = useAccountId();

  return useCallback(
    (catalogItemUuid: string, section?: SongPageSection, view?: SongLyricsView) => {
      if (catalogItemUuid === '') {
        return;
      }
      if (isNativeEmbed) {
        navigateNative(songFilesDeepLink(catalogItemUuid, { section }));
        return;
      }
      // eslint-disable-next-line no-void
      void navigate(catalogSongPath(catalogItemUuid, { section, accountId, view }));
    },
    [accountId, isNativeEmbed, navigate, navigateNative]
  );
}
